/**
 * Ditto stores an optional avatar *shape* in kind-0 metadata under the `shape`
 * property: a single emoji whose glyph is used as an alpha mask for the avatar
 * (see Ditto's `src/lib/avatarShape.ts`). When absent, avatars are circles.
 */
export type AvatarShape = string;

/**
 * Rather than matching specific Unicode emoji ranges (fragile, and it excludes
 * keycaps, flags and ZWJ families), accept any short non-ASCII string.
 */
export function isEmoji(value: string): boolean {
  if (!value) return false;
  // Even complex ZWJ sequences stay under ~20 JS chars.
  if (value.length > 20) return false;
  // eslint-disable-next-line no-control-regex
  return /[^\x00-\x7F]/.test(value);
}

/** Read a valid shape out of kind-0 metadata, or `undefined` for "circle". */
export function getAvatarShape(
  metadata: { [key: string]: unknown } | undefined,
): AvatarShape | undefined {
  const raw = metadata?.shape;
  return typeof raw === 'string' && isEmoji(raw) ? raw : undefined;
}

/** In-memory cache: emoji string -> mask data-URL. */
const maskCache = new Map<string, string>();

/**
 * Render the emoji to a canvas and return a PNG data-URL alpha mask suitable
 * for CSS `mask-image`.
 *
 * The glyph is drawn oversized, its non-transparent bounding box is measured
 * and squared (so non-square emoji aren't stretched), then redrawn to fill a
 * 256x256 output whose RGB is forced to white with the original alpha kept.
 *
 * Returns `''` when the mask can't be produced; callers should fall back to a
 * plain circle.
 */
export function getEmojiMaskUrl(emoji: string): string {
  const cached = maskCache.get(emoji);
  if (cached !== undefined) return cached;
  if (typeof document === 'undefined') return '';

  const fontSize = 512;
  const scratch = fontSize * 1.5;
  const c1 = document.createElement('canvas');
  c1.width = scratch;
  c1.height = scratch;
  const ctx1 = c1.getContext('2d', { willReadFrequently: true });
  if (!ctx1) return '';

  ctx1.textAlign = 'center';
  ctx1.textBaseline = 'middle';
  ctx1.font = `${fontSize}px serif`;
  ctx1.fillText(emoji, scratch / 2, scratch / 2);

  // Ignore anti-aliasing fringes and drop shadows, which would otherwise
  // inflate the bounding box and push the glyph off-center once squared.
  const ALPHA_THRESHOLD = 25;
  const { data: px } = ctx1.getImageData(0, 0, scratch, scratch);
  let top = scratch, bottom = 0, left = scratch, right = 0;
  for (let y = 0; y < scratch; y++) {
    for (let x = 0; x < scratch; x++) {
      if (px[(y * scratch + x) * 4 + 3] > ALPHA_THRESHOLD) {
        if (y < top) top = y;
        if (y > bottom) bottom = y;
        if (x < left) left = x;
        if (x > right) right = x;
      }
    }
  }
  if (right < left || bottom < top) return '';

  let cropW = right - left + 1;
  let cropH = bottom - top + 1;
  if (cropW > cropH) {
    top -= Math.floor((cropW - cropH) / 2);
    cropH = cropW;
  } else if (cropH > cropW) {
    left -= Math.floor((cropH - cropW) / 2);
    cropW = cropH;
  }
  if (top < 0) top = 0;
  if (left < 0) left = 0;

  const out = 256;
  const c2 = document.createElement('canvas');
  c2.width = out;
  c2.height = out;
  const ctx2 = c2.getContext('2d', { willReadFrequently: true });
  if (!ctx2) return '';
  ctx2.drawImage(c1, left, top, cropW, cropH, 0, 0, out, out);

  const img = ctx2.getImageData(0, 0, out, out);
  const d = img.data;
  for (let i = 0; i < d.length; i += 4) {
    d[i] = 255;
    d[i + 1] = 255;
    d[i + 2] = 255;
    // alpha preserved
  }
  ctx2.putImageData(img, 0, 0);

  const url = c2.toDataURL('image/png');
  maskCache.set(emoji, url);
  return url;
}
